/**
 * DG CUSTOM (#15): "Call us to check if it's available at <local store> <phone>".
 *
 * Shown under the buy button when the selected variant is sold out online, so a shopper who
 * hits a dead "Sold Out Online. Check in Store" button is told exactly who to ring.
 *
 * The store is resolved against the store_location metaobject coordinates already embedded
 * in the page; the distance comparison itself always happens here in the browser.
 *
 * Position and nearest-store resolution now live in DG-store-locate.js, shared with the store
 * locator (#45) so both surfaces resolve identically and share one cache. Read that file for the
 * accept / ignore / dismiss / block rules and which single step is billable.
 *
 * What stays here is only what is specific to this note: when to show it, and how to render the
 * merchant's copy. The server-rendered fallback text is what remains for a shopper who never
 * answered the permission prompt, and for the genuine dead ends — no key configured, the lookup
 * failing, or no store having a phone number.
 */
if (!customElements.get('store-stock-note')) {
  class StoreStockNote extends HTMLElement {
    connectedCallback() {
      this.resolved = this.querySelector('[data-store-stock-resolved]');
      this.fallback = this.querySelector('[data-store-stock-fallback]');
      this.template = this.dataset.template || '';
      this.alwaysVisible = this.dataset.visibility === 'always';

      const data = this.querySelector('[data-store-stock-stores]');

      try {
        this.stores = data ? JSON.parse(data.textContent) : [];
      } catch (e) {
        this.stores = [];
      }

      // Only stores we can actually tell someone to ring are candidates.
      this.stores = this.stores.filter((store) => store.p && store.c);

      this.section = this.closest('.js-product') || this.closest('.cc-main-product') || document;
      this.onVariantChange = this.handleVariantChange.bind(this);
      this.section.addEventListener('on:variant:change', this.onVariantChange);

      // The server already decided the opening state from current_variant, so a sold-out
      // landing (?variant=… on an OOS variant) resolves a store without waiting for an event.
      if (!this.hidden) this.resolveStore();
    }

    disconnectedCallback() {
      this.section.removeEventListener('on:variant:change', this.onVariantChange);
    }

    /**
     * Shows or hides the note as the shopper moves between variants.
     *
     * Deliberately narrow: a variant that EXISTS but is unavailable is the only case a store
     * could still have stock of. A combination that was never made (variant undefined, button
     * reads "Unavailable") is not sold out anywhere, so pointing a shopper at a phone number
     * would waste their call.
     * @param {CustomEvent} evt - The on:variant:change event.
     */
    handleVariantChange(evt) {
      const variant = evt.detail ? evt.detail.variant : null;
      const soldOutOnline = Boolean(variant) && !variant.available;

      this.hidden = !(this.alwaysVisible || soldOutOnline);
      if (!this.hidden) this.resolveStore();
    }

    /**
     * Resolves the nearest store, preferring a previously resolved one.
     */
    async resolveStore() {
      if (this.rendered || !this.stores.length) return;

      const cached = DGStoreLocate.readCache();
      if (cached) {
        this.render(cached);
        return;
      }

      // Don't retry for the rest of the session once a lookup has genuinely failed. Set only
      // below, where every option really has been exhausted — never for a shopper who simply has
      // not answered yet, which used to poison the whole session: dismiss the bubble on one PDP
      // and blocking it on the next silently did nothing at all.
      if (sessionStorage.getItem(DGStoreLocate.DECLINED_KEY)) return;

      // Claimed up front so rapid variant changes cannot fire two lookups — a lookup can be
      // billable, so a double-fire costs money as well as being wasteful.
      this.rendered = true;

      const position = await DGStoreLocate.resolvePosition(this.dataset.geoKey);

      if (!this.showNearest(position)) {
        // The server-rendered fallback is already on screen, so there is nothing to swap in.
        //
        // Only remember the failure once the shopper has actually answered. A dismissed bubble
        // leaves the permission at 'prompt' and decides nothing, and flagging that killed the rest
        // of the session: a genuine Block on the very next PDP found the flag and never even
        // tried, so blocking looked permanently broken to anyone who had dismissed once.
        if ((await DGStoreLocate.permissionState()) !== 'prompt') {
          sessionStorage.setItem(DGStoreLocate.DECLINED_KEY, '1');
        }

        // `rendered` deliberately stays claimed either way, so an unanswered prompt is not
        // re-issued on every variant change.
      }
    }

    /**
     * Renders the nearest store to a position, if that position yields one.
     * @param {object|null} position - A resolved position, or null.
     * @returns {boolean} Whether a store was rendered.
     */
    showNearest(position) {
      const nearest = position
        ? DGStoreLocate.byDistance(this.stores, position, (store) => store.c)[0]
        : null;

      if (!nearest) return false;

      sessionStorage.removeItem(DGStoreLocate.DECLINED_KEY);
      DGStoreLocate.writeCache(nearest.store, position.src);
      this.render(nearest.store);
      return true;
    }

    /**
     * Swaps the fallback for the resolved copy.
     *
     * Built from text nodes and elements rather than innerHTML: the store name and phone
     * number come from merchant data, and the phone has to be a tel: link on mobile anyway.
     * @param {object} store - The resolved store.
     */
    render(store) {
      this.rendered = true;
      this.resolved.textContent = '';

      // Split on the placeholders but keep them, so {store}/{phone} can appear in any order.
      const parts = this.template.split(/(\{store\}|\{phone\})/);

      parts.forEach((part) => {
        if (part === '{store}') {
          const name = store.u ? document.createElement('a') : document.createElement('span');
          if (store.u) name.href = store.u;
          name.className = 'store-stock-note__store';
          name.textContent = store.t;
          this.resolved.appendChild(name);
        } else if (part === '{phone}') {
          const tel = document.createElement('a');
          tel.href = `tel:${String(store.p).replace(/[^\d+]/g, '')}`;
          tel.className = 'store-stock-note__phone';
          tel.textContent = store.p;
          this.resolved.appendChild(tel);
        } else if (part) {
          this.resolved.appendChild(document.createTextNode(part));
        }
      });

      this.resolved.hidden = false;
      this.fallback.hidden = true;
    }
  }

  customElements.define('store-stock-note', StoreStockNote);
}
